import { useCallback, useId, useMemo, useState } from "react"
import { DescriptionOutlined } from "@mui/icons-material"
import { Alert, Box, Button, Chip, chipClasses, CircularProgress, List, ListItem, ListItemButton, ListItemIcon, ListItemText, ListSubheader, Typography } from "@mui/material"
import type { ListItemTextProps, SxProps, Theme } from "@mui/material"
import { visuallyHidden } from "@mui/utils"
import { AI_TEAMMATE_EVENTS } from "connected-components/ai-teammate/analytics/events"
import { useAnalytics } from "connected-components/analytics"
import { useAppDispatch, useAppSelector } from "hooks/redux"
import { useUserId } from "resources/account"
import { useScheduledWorkflowDeliverables, useScheduledWorkflowDeliverablesUnreadCount, useScheduledWorkflowRuns, useScheduledWorkflows } from "resources/scheduled-workflows/hooks"
import type { ScheduledWorkflowDeliverableListItem, ScheduledWorkflowListItem, ScheduledWorkflowRun } from "resources/scheduled-workflows/types"
import { CollapsibleSection, SectionMoreButton, useSidePanelSurface } from "../chatbox/collapsible-section"
import { SUBHEADER_SX } from "../chatbox/side-panel"
import { selectSectionCollapsed, setSectionCollapsed } from "../store/agent-chat-slice"
import { reportAccessOf, reportEntryLabel, sharedReportMarker, UNREAD_DOT_SX } from "./report-format"
import { latestReportLabel, latestReportOf } from "./latest-report"
import { formatReportTime, viewerTimeZone } from "./schedule-format"
import { useNow } from "./use-now"

const MESSAGE_SX = { m: 1 } satisfies SxProps<Theme>
const EMPTY_SX = { px: 2, py: 1, color: "text.secondary", typography: "body2" } satisfies SxProps<Theme>
const LOADING_SX = { display: "flex", justifyContent: "center", p: 2 } satisfies SxProps<Theme>
const SHOW_MORE_SX = { justifyContent: "flex-start", textTransform: "none", mx: 1 } satisfies SxProps<Theme>
const COMPACT_PILL_SX = {
  height: 16, fontSize: "0.75rem", fontWeight: 600, lineHeight: 1, pb: "0.5px",
  [`& .${chipClasses.label}`]: { px: 0.75 },
} satisfies SxProps<Theme>
const UNREAD_PILL_SX = { ...COMPACT_PILL_SX, ml: "auto" } satisfies SxProps<Theme>
const UNREAD_PILL_PREVIEW_SX = { ...COMPACT_PILL_SX, ml: 1 } satisfies SxProps<Theme>
const ICON_SX = { minWidth: 0, mr: 1.5 } satisfies SxProps<Theme>
const UNREAD_TITLE_SLOT_PROPS = { primary: { sx: { fontWeight: 700 } } } satisfies NonNullable<ListItemTextProps["slotProps"]>

const VIEWER_ZONE = viewerTimeZone()
const EMPTY_RUNS: ScheduledWorkflowRun[] = []

export const REPORTS_VISIBLE_LIMIT = 5

interface ReportLine {
  deliverableId: string
  workflowName: string
  createdAt: string
  read: boolean
  sharedMarker: string | undefined
}

function buildReportLines(workflows: ScheduledWorkflowListItem[], loadedReports: ScheduledWorkflowDeliverableListItem[], viewerUserId: string | undefined): ReportLine[] {
  const ownLines = workflows.flatMap((workflow) => workflow.latestDeliverable == null
    ? []
    : [{ ...workflow.latestDeliverable, workflowName: workflow.name, sharedMarker: undefined }])
  const latestShared = new Map<string, ReportLine>()
  for (const report of loadedReports) {
    const access = reportAccessOf(report, viewerUserId)
    if (access.kind !== "sharee") continue
    const current = latestShared.get(report.workflowId)
    if (current != null && Date.parse(current.createdAt) >= Date.parse(report.createdAt)) continue
    latestShared.set(report.workflowId, { deliverableId: report.deliverableId, workflowName: report.workflowName, createdAt: report.createdAt, read: report.read, sharedMarker: sharedReportMarker(access) })
  }
  return [...ownLines, ...latestShared.values()].sort((first, second) => Date.parse(second.createdAt) - Date.parse(first.createdAt))
}

export function ReportsSection({ onOpen, onOpenAll, variant = "default" }: { onOpen: (deliverableId: string) => void, onOpenAll?: () => void, variant?: "default" | "preview" }) {
  const headingId = useId()
  const dispatch = useAppDispatch()
  const isCollapsed = useAppSelector((state) => selectSectionCollapsed(state, "reports"))
  const { workflows, isLoading, isError, isIncomplete } = useScheduledWorkflows({ includeArchived: true })
  const viewerUserId = useUserId()
  const { data: reportPages, isError: isSharedError, refetch: refetchShared } = useScheduledWorkflowDeliverables()
  const isSharedPending = reportPages == null && !isSharedError
  const retryShared = useCallback(() => { void refetchShared() }, [refetchShared])
  const { data: unreadCount } = useScheduledWorkflowDeliverablesUnreadCount()
  const unread = unreadCount?.count ?? 0
  const deliverables = useMemo(
    () => buildReportLines(workflows ?? [], reportPages?.pages.flatMap((page) => page.deliverables) ?? [], viewerUserId),
    [workflows, reportPages, viewerUserId],
  )
  const [isShowingAll, setIsShowingAll] = useState(false)
  const hasOverflow = deliverables.length > REPORTS_VISIBLE_LIMIT
  const [hadOverflow, setHadOverflow] = useState(hasOverflow)
  if (hasOverflow !== hadOverflow) {
    setHadOverflow(hasOverflow)
    if (!hasOverflow) setIsShowingAll(false)
  }
  const visibleDeliverables = isShowingAll ? deliverables : deliverables.slice(0, REPORTS_VISIBLE_LIMIT)
  const hiddenCount = deliverables.length - visibleDeliverables.length
  const listId = `${headingId}-list`
  const toggleShowAll = useCallback(() => setIsShowingAll((showingAll) => !showingAll), [])
  const analytics = useAnalytics()
  const surface = useSidePanelSurface()
  const toggleCollapsed = useCallback(() => {
    analytics.track(AI_TEAMMATE_EVENTS.THREAD_SECTION_TOGGLED, { section: "reports", collapsed: !isCollapsed, ...(surface == null ? {} : { surface }) })
    dispatch(setSectionCollapsed({ section: "reports", collapsed: !isCollapsed }))
  }, [analytics, dispatch, isCollapsed, surface])
  const unreadPill = unread > 0 && <Chip size="small" color="primary" sx={variant === "preview" ? UNREAD_PILL_PREVIEW_SX : UNREAD_PILL_SX} label={unread > 999 ? "999+" : unread} data-testid="reports-unread-pill" />
  const reportLines = (
    <>
      {isError && workflows == null && <Alert severity="error" sx={MESSAGE_SX}>Couldn&apos;t load your reports. Please try again later.</Alert>}
      {(isLoading || (workflows != null && isSharedPending && deliverables.length === 0)) && (
        <Box sx={LOADING_SX}>
          <CircularProgress size={20} aria-label="Loading reports" />
        </Box>
      )}
      {workflows != null && !isLoading && reportPages != null && deliverables.length === 0 && <Typography sx={EMPTY_SX}>Reports land here once a workflow runs.</Typography>}
      <List id={listId} dense disablePadding>
        {visibleDeliverables.map((deliverable) => (
          <ListItemButton key={deliverable.deliverableId} onClick={(event) => { event.currentTarget.focus(); onOpen(deliverable.deliverableId) }}>
            <ListItemIcon sx={ICON_SX}>
              {deliverable.read
                ? <DescriptionOutlined fontSize="small" data-testid="report-read-icon" />
                : <Box sx={UNREAD_DOT_SX} aria-hidden data-testid="report-unread-dot" />}
            </ListItemIcon>
            <ListItemText
              primary={deliverable.read ? reportEntryLabel(deliverable, VIEWER_ZONE) : <><Box component="span" sx={visuallyHidden}>Unread: </Box>{reportEntryLabel(deliverable, VIEWER_ZONE)}</>}
              secondary={deliverable.sharedMarker}
              slotProps={deliverable.read ? undefined : UNREAD_TITLE_SLOT_PROPS}
            />
          </ListItemButton>
        ))}
      </List>
      {deliverables.length > 0 && onOpenAll != null && <Button size="small" sx={SHOW_MORE_SX} onClick={onOpenAll} data-testid="chatbox-reports-all">All reports</Button>}
      {isIncomplete && <Alert severity="error" sx={MESSAGE_SX}>Couldn&apos;t load more reports.</Alert>}
      {isSharedError && reportPages == null && workflows != null && (
        <Alert severity="error" sx={MESSAGE_SX} action={<Button size="small" onClick={retryShared}>Try again</Button>}>Couldn&apos;t load reports shared with you.</Alert>
      )}
      {hasOverflow && (
        <SectionMoreButton
          onClick={toggleShowAll}
          testId="chatbox-reports-show-less"
          disclosure={{ isExpanded: isShowingAll, controlsId: listId }}
        >
          {isShowingAll ? "Show less" : `Show ${hiddenCount} more`}
        </SectionMoreButton>
      )}
    </>
  )

  if (variant === "preview") {
    return (
      <Box component="section" aria-labelledby={headingId} data-testid="chatbox-reports-section">
        <ListSubheader id={headingId} component="div" disableSticky sx={SUBHEADER_SX}>{workflows == null ? "Reports" : `Reports (${deliverables.length})`}{unreadPill}</ListSubheader>
        {reportLines}
      </Box>
    )
  }

  return (
    <CollapsibleSection
      headingId={headingId}
      label="Reports"
      count={workflows == null ? undefined : `${deliverables.length}`}
      adornment={unreadPill}
      isCollapsed={isCollapsed}
      onToggleCollapse={toggleCollapsed}
      testId="chatbox-reports-section"
    >
      {reportLines}
    </CollapsibleSection>
  )
}

interface RunHistoryProps {
  workflowId: string
  onOpenReport: (deliverableId: string) => void
}

export function RunHistory({ workflowId, onOpenReport }: RunHistoryProps) {
  const headingId = useId()
  const { data, isLoading, isError, isFetchNextPageError, hasNextPage, isFetchingNextPage, fetchNextPage, refetch } = useScheduledWorkflowRuns(workflowId)
  const runs = data?.pages.flatMap((page) => page.runs) ?? EMPTY_RUNS
  const showMore = useCallback(() => { void fetchNextPage() }, [fetchNextPage])
  const now = useNow()

  return (
    <Box component="section" aria-labelledby={headingId} data-testid="workflow-run-history">
      <ListSubheader id={headingId} component="div" disableSticky sx={SUBHEADER_SX}>All runs</ListSubheader>
      {isError && data == null && <Alert severity="error" sx={MESSAGE_SX} action={<Button size="small" onClick={() => refetch()}>Try again</Button>}>Couldn&apos;t load the runs. Please try again later.</Alert>}
      {isLoading && (
        <Box sx={LOADING_SX}>
          <CircularProgress size={20} aria-label="Loading runs" />
        </Box>
      )}
      {data != null && runs.length === 0 && <Typography sx={EMPTY_SX}>No runs yet</Typography>}
      <List dense disablePadding>
        {runs.map((run) => {
          const deliverable = run.deliverable
          const label = latestReportLabel(latestReportOf(run, deliverable == null ? null : { ...deliverable, createdAt: run.createdAt, read: true }), (time) => formatReportTime(time, now, VIEWER_ZONE))
          const primary = [label, run.retryOfRunId == null ? null : "Retry"].filter((part) => part != null).join(" · ")
          if (deliverable == null) {
            const agentWroteReason = run.outcome === "succeeded_empty" || run.outcome === "failed"
            return (
              <ListItem key={run.runId}>
                <ListItemText primary={primary} secondary={agentWroteReason ? run.outcomeReason : null} />
              </ListItem>
            )
          }
          return (
            <ListItemButton key={run.runId} onClick={(event) => { event.currentTarget.focus(); onOpenReport(deliverable.deliverableId) }}>
              <ListItemText primary={primary} />
            </ListItemButton>
          )
        })}
      </List>
      {isFetchNextPageError && (
        <Alert severity="error" sx={MESSAGE_SX} action={<Button size="small" onClick={showMore}>Try again</Button>}>Couldn&apos;t load more runs.</Alert>
      )}
      {hasNextPage && !isFetchNextPageError && (
        <Button variant="text" size="small" sx={SHOW_MORE_SX} disabled={isFetchingNextPage} onClick={showMore}>
          Show more
        </Button>
      )}
    </Box>
  )
}
